import { useEffect, useState } from "react";
import type { AmbientMood } from "@/hooks/useAmbientState";

interface AuroraMeshProps {
  /** Triggers a brief warm bloom. Pass an incrementing key/timestamp. */
  bloomKey?: number | null;
  /** Ambient mood — drives a subtle color overlay */
  mood?: AmbientMood;
  className?: string;
}

/**
 * 3-layer animated mesh gradient. Pure CSS — no canvas, no rAF.
 * Reacts to time-of-day (cooler at night) and bloom events.
 */
export function AuroraMesh({ bloomKey, mood = "calm", className }: AuroraMeshProps) {
  const [bloom, setBloom] = useState(false);
  const hour = new Date().getHours();
  const isNight = hour < 7 || hour >= 20;

  useEffect(() => {
    if (!bloomKey) return;
    setBloom(true);
    const t = setTimeout(() => setBloom(false), 2200);
    return () => clearTimeout(t);
  }, [bloomKey]);

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className ?? ""}`}
      style={{
        opacity: isNight ? 0.85 : 1,
        filter: isNight ? "hue-rotate(-12deg)" : undefined,
      }}
    >
      <div className="aurora-layer aurora-layer-1" />
      <div className="aurora-layer aurora-layer-2" />
      <div className="aurora-layer aurora-layer-3" />
      <div className={`aurora-mood aurora-mood-${mood}`} />
      {bloom && (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 40%, hsl(var(--primary-glow) / 0.35), transparent 70%)",
            animation: "breathe 2.2s ease-out forwards",
            opacity: 1,
          }}
        />
      )}
    </div>
  );
}
